(() => {
  if (window.plugBackInStock?.initialized) return;

  const CONFIG_SELECTOR = "[data-product-back-in-stock-config]";
  const TEMPLATE_SELECTOR = "[data-product-back-in-stock-template]";
  const ROOT_SELECTOR = "[data-product-back-in-stock-root]";
  const FORM_WRAPPER_SELECTOR = "product-form, pf-pilot-product-form";
  const EXCLUDED_FORM_CONTEXT_SELECTOR = [
    ".quick-add",
    "quick-add-modal",
    "product-recommendations",
    "product-upsell",
    ".card-wrapper",
    ".product-card-wrapper",
    "[data-pf-scroll-variant-source]",
  ].join(", ");
  const OPEN_EVENT = "plug:back-in-stock-open";
  const READY_EVENT = "plug:back-in-stock-ready";
  const ANALYTICS_EVENTS = {
    impression: "plug_pdp_back_in_stock_impression",
    clicked: "plug_pdp_back_in_stock_cta_clicked",
    attempted: "plug_pdp_back_in_stock_submit_attempted",
    succeeded: "plug_pdp_back_in_stock_subscription_succeeded",
    failed: "plug_pdp_back_in_stock_subscription_failed",
  };

  let configElement = document.querySelector(CONFIG_SELECTOR);
  let template = document.querySelector(TEMPLATE_SELECTOR);
  if (!configElement || !template) return;

  const decodeLocalizedText = (value) => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = String(value || "");
    return textarea.value;
  };

  const parseConfiguration = (element) => {
    try {
      const parsed = JSON.parse(element.textContent);
      parsed.labels = Object.fromEntries(
        Object.entries(parsed.labels || {}).map(([key, value]) => [key, decodeLocalizedText(value)])
      );
      return parsed;
    } catch {
      return null;
    }
  };

  let config = parseConfiguration(configElement);
  if (!config) return;

  const state = {
    activeRequests: new Map(),
    email: "",
    formRecords: new Map(),
    impressionKeys: new Set(),
    latestVariant: null,
    pendingOpen: null,
    productForms: [],
    ready: false,
    renderQueued: false,
    requestSequence: 0,
    rootRecords: new WeakMap(),
    rootSequence: 0,
    successfulVariants: new Set(),
  };

  let variantMap = new Map(
    (Array.isArray(config.variants) ? config.variants : []).map((variant) => [
      String(variant.id),
      variant,
    ])
  );

  const refreshConfiguration = () => {
    const nextConfigElement = document.querySelector(CONFIG_SELECTOR);
    const nextTemplate = document.querySelector(TEMPLATE_SELECTOR);
    if (!nextConfigElement || !nextTemplate) return false;
    if (nextConfigElement !== configElement) {
      const nextConfig = parseConfiguration(nextConfigElement);
      if (!nextConfig) return false;
      configElement = nextConfigElement;
      config = nextConfig;
      variantMap = new Map(
        (Array.isArray(config.variants) ? config.variants : []).map((variant) => [
          String(variant.id),
          variant,
        ])
      );
      if (window.plugBackInStock) {
        window.plugBackInStock.labels = { ...config.labels };
      }
    }
    template = nextTemplate;
    return true;
  };

  const isVisible = (element) => {
    if (!element || !element.isConnected || element.closest("[hidden], [inert]")) return false;
    const style = window.getComputedStyle(element);
    return (
      style.display !== "none" &&
      style.visibility !== "hidden" &&
      element.getClientRects().length > 0
    );
  };

  const getProductForms = () =>
    Array.from(document.querySelectorAll(FORM_WRAPPER_SELECTOR)).filter((productForm) => {
      const addToCartForm = productForm.querySelector('form[data-type="add-to-cart-form"]');
      if (!addToCartForm || productForm.closest(EXCLUDED_FORM_CONTEXT_SELECTOR)) return false;
      if (/^(quick-add-|product-form-installment-)/.test(addToCartForm.id)) return false;
      const productInfo = productForm.closest("product-info, pf-pilot-product-info");
      return Boolean(productInfo?.matches?.('[data-pf-scroll-product-info], [id^="MainProduct-"]'));
    });

  const restorePurchaseControls = (productForm) => {
    productForm
      ?.querySelectorAll("[data-back-in-stock-hidden]")
      .forEach((control) => control.removeAttribute("data-back-in-stock-hidden"));
  };

  const hidePurchaseControls = (productForm) => {
    productForm
      ?.querySelectorAll('button[name="add"], .product-form__submit-seal, .shopify-payment-button')
      .forEach((control) => control.setAttribute("data-back-in-stock-hidden", ""));
  };

  const parseVariantJSON = (productForm) => {
    const productInfo = productForm?.closest("product-info, pf-pilot-product-info");
    const selectedVariant = productInfo?.querySelector(
      'variant-selects:not([data-upsell-isolated="true"]) [data-selected-variant]'
    );
    if (!selectedVariant?.textContent?.trim()) return null;
    try {
      return JSON.parse(selectedVariant.textContent.trim());
    } catch {
      return null;
    }
  };

  const getVariantId = (productForm) =>
    productForm?.querySelector('form[data-type="add-to-cart-form"] input[name="id"]')?.value || "";

  const resolveVariant = (productForm, hintedVariant = null) => {
    const variantId = String(getVariantId(productForm));
    if (!variantId) return null;
    if (hintedVariant && String(hintedVariant.id) === variantId) return hintedVariant;
    const parsedVariant = parseVariantJSON(productForm);
    if (parsedVariant && String(parsedVariant.id) === variantId) return parsedVariant;
    return variantMap.get(variantId) || null;
  };

  const getVariantSummary = (variant) => {
    const options = (Array.isArray(variant?.options) ? variant.options : [])
      .map((option) => String(option || "").trim())
      .filter((option) => option && option.toLowerCase() !== "default title");
    if (options.length) return options.join(" / ");
    const title = String(variant?.title || "").trim();
    if (title && title.toLowerCase() !== "default title") return title;
    return config.productTitle || "";
  };

  const getProductHandle = (productForm) => {
    const rawUrl = productForm?.closest("product-info, pf-pilot-product-info")?.dataset?.url;
    if (rawUrl) {
      try {
        const pathParts = new URL(rawUrl, window.location.origin).pathname
          .split("/")
          .filter(Boolean);
        const productIndex = pathParts.indexOf("products");
        if (productIndex !== -1 && pathParts[productIndex + 1]) return pathParts[productIndex + 1];
      } catch {
        return config.productHandle || "";
      }
    }
    return config.productHandle || "";
  };

  const getAnalyticsContext = (variant, surface = "buy_box", productForm = null) => ({
    product_id:
      productForm?.dataset?.productId ||
      productForm?.closest("[data-pf-selected-product-id]")?.dataset?.pfSelectedProductId ||
      variant?.product_id ||
      config.productId,
    variant_id: variant?.id || getVariantId(productForm),
    product_handle: getProductHandle(productForm),
    template_suffix: config.templateSuffix || "default",
    variant_title: getVariantSummary(variant),
    surface,
  });

  const track = (eventName, variant, surface, productForm) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...getAnalyticsContext(variant, surface, productForm),
    });
  };

  const setReady = (ready) => {
    if (state.ready === ready) return;
    state.ready = ready;
    document.documentElement.classList.toggle("product-back-in-stock-ready", ready);
    if (window.plugBackInStock) window.plugBackInStock.ready = ready;
    document.dispatchEvent(
      new CustomEvent(READY_EVENT, {
        detail: { labels: { ...config.labels }, ready },
      })
    );
  };

  const successMessage = (variant) =>
    String(config.labels.success || "").replace("__VARIANT__", getVariantSummary(variant));

  const setMessage = (record, message, status = "success") => {
    const element = record?.root?.querySelector("[data-back-in-stock-message]");
    if (!element) return;
    if (element.textContent !== message) element.textContent = message;
    element.dataset.state = status;
    element.hidden = !message;
  };

  const getRecordsForVariant = (variantId) =>
    Array.from(state.formRecords.values()).filter(
      (record) => String(record.currentVariant?.id || "") === String(variantId)
    );

  const mirrorEmail = (sourceInput) => {
    state.email = sourceInput.value;
    state.formRecords.forEach((record) => {
      const emailInput = record.root?.querySelector("[data-back-in-stock-email]");
      if (emailInput && emailInput !== sourceInput && emailInput.value !== state.email) {
        emailInput.value = state.email;
      }
    });
  };

  const syncRecordControls = (record) => {
    const currentVariantId = String(record.currentVariant?.id || "");
    const submitting = state.activeRequests.has(currentVariantId);
    const successful = state.successfulVariants.has(currentVariantId);
    const toggle = record.root?.querySelector("[data-back-in-stock-toggle]");
    const form = record.root?.querySelector("[data-back-in-stock-form]");
    const emailInput = record.root?.querySelector("[data-back-in-stock-email]");
    const submit = record.root?.querySelector("[data-back-in-stock-submit]");
    const submitLabel = record.root?.querySelector("[data-back-in-stock-submit-label]");
    if (form) form.setAttribute("aria-busy", String(submitting));
    if (toggle) toggle.disabled = submitting;
    if (emailInput) emailInput.disabled = submitting || successful;
    if (submit) submit.disabled = submitting || successful;
    const nextSubmitLabel = submitting ? config.labels.loading : config.labels.submit;
    if (submitLabel && submitLabel.textContent !== nextSubmitLabel) {
      submitLabel.textContent = nextSubmitLabel;
    }
    if (successful && record.currentVariant) {
      const helper = record.root?.querySelector("[data-back-in-stock-helper]");
      if (toggle) toggle.hidden = true;
      if (helper) helper.hidden = true;
      if (form) form.hidden = false;
      setMessage(record, successMessage(record.currentVariant));
    }
  };

  const syncVariantRecords = (variantId) => {
    getRecordsForVariant(variantId).forEach(syncRecordControls);
  };

  const focusSuccessMessage = (record) => {
    const message = record?.root?.querySelector("[data-back-in-stock-message]");
    if (!message || message.hidden) return;
    message.focus({ preventScroll: true });
  };

  const openRecord = (record, { focus = true, scroll = false, surface = "buy_box" } = {}) => {
    if (
      !state.ready ||
      !record?.root?.isConnected ||
      record.root.hidden ||
      record.currentVariant?.available !== false
    ) {
      return false;
    }
    record.surface = surface;
    const toggle = record.root.querySelector("[data-back-in-stock-toggle]");
    const form = record.root.querySelector("[data-back-in-stock-form]");
    const emailInput = record.root.querySelector("[data-back-in-stock-email]");
    if (!toggle || !form || !emailInput) return false;
    toggle.setAttribute("aria-expanded", "true");
    toggle.hidden = true;
    form.hidden = false;
    if (scroll) record.root.scrollIntoView({ behavior: "smooth", block: "center" });
    if (focus) window.requestAnimationFrame(() => emailInput.focus({ preventScroll: true }));
    return true;
  };

  const getPreferredRecord = (requestedVariantId = "") => {
    const records = Array.from(state.formRecords.values()).filter((record) => {
      if (!record.root?.isConnected || record.root.hidden || !isVisible(record.productForm)) {
        return false;
      }
      if (record.currentVariant?.available !== false) return false;
      return !requestedVariantId || String(record.currentVariant.id) === String(requestedVariantId);
    });
    return (
      records.find((record) => record.productForm.closest("[data-pf-scroll-product-info]")) ||
      records[0] ||
      null
    );
  };

  const openPreferredRecord = (options = {}, requestedVariantId = "") => {
    const record = getPreferredRecord(requestedVariantId);
    return record && openRecord(record, options) ? record : null;
  };

  const bindRoot = (record) => {
    state.rootSequence += 1;
    const sequence = `${configElement.dataset.sectionId || "main"}-${state.rootSequence}`;
    const { root } = record;
    const toggle = root.querySelector("[data-back-in-stock-toggle]");
    const form = root.querySelector("[data-back-in-stock-form]");
    const label = root.querySelector("[data-back-in-stock-label]");
    const emailInput = root.querySelector("[data-back-in-stock-email]");
    const helper = root.querySelector("[data-back-in-stock-helper]");
    const message = root.querySelector("[data-back-in-stock-message]");
    if (!toggle || !form || !label || !emailInput || !helper || !message) return false;

    form.id = `ProductBackInStockForm-${sequence}`;
    emailInput.id = `ProductBackInStockEmail-${sequence}`;
    helper.id = `ProductBackInStockHelper-${sequence}`;
    message.id = `ProductBackInStockMessage-${sequence}`;
    label.htmlFor = emailInput.id;
    toggle.setAttribute("aria-controls", form.id);
    emailInput.setAttribute("aria-describedby", `${helper.id} ${message.id}`);

    toggle.addEventListener("click", () => {
      if (openRecord(record, { surface: "buy_box" })) {
        track(ANALYTICS_EVENTS.clicked, record.currentVariant, "buy_box", record.productForm);
      }
    });

    emailInput.addEventListener("input", () => mirrorEmail(emailInput));

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const submittedVariant = record.currentVariant;
      if (!submittedVariant || submittedVariant.available !== false) return;
      const variantId = String(submittedVariant.id || "");
      if (state.activeRequests.has(variantId) || state.successfulVariants.has(variantId)) return;
      if (!emailInput.checkValidity()) {
        emailInput.reportValidity();
        return;
      }

      const submittedProductForm = record.productForm;
      const submittedSurface = record.surface || "buy_box";
      const email = emailInput.value.trim();
      if (!email || !variantId) return;

      const request = {
        token: ++state.requestSequence,
        variantId,
      };
      state.activeRequests.set(variantId, request);
      track(ANALYTICS_EVENTS.attempted, submittedVariant, submittedSurface, submittedProductForm);
      getRecordsForVariant(variantId).forEach((matchingRecord) => setMessage(matchingRecord, ""));
      syncVariantRecords(variantId);

      let succeeded = false;
      let failed = false;

      try {
        const response = await fetch(
          `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(
            config.companyId
          )}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              revision: "2024-06-15",
            },
            body: JSON.stringify({
              data: {
                type: "back-in-stock-subscription",
                attributes: {
                  profile: {
                    data: {
                      type: "profile",
                      attributes: { email },
                    },
                  },
                  channels: ["EMAIL"],
                },
                relationships: {
                  variant: {
                    data: {
                      type: "catalog-variant",
                      id: `$shopify:::$default:::${variantId}`,
                    },
                  },
                },
              },
            }),
          }
        );
        if (!response.ok) throw new Error(String(response.status));
        succeeded = true;
        state.successfulVariants.add(variantId);
        track(ANALYTICS_EVENTS.succeeded, submittedVariant, submittedSurface, submittedProductForm);
      } catch {
        failed = true;
        track(ANALYTICS_EVENTS.failed, submittedVariant, submittedSurface, submittedProductForm);
      } finally {
        if (state.activeRequests.get(variantId)?.token === request.token) {
          state.activeRequests.delete(variantId);
        }
        syncVariantRecords(variantId);
        if (succeeded && String(record.currentVariant?.id) === variantId) {
          focusSuccessMessage(record);
        } else if (failed && String(record.currentVariant?.id) === variantId) {
          setMessage(record, config.labels.error, "error");
        }
      }
    });

    return true;
  };

  const ensureRecord = (productForm) => {
    const existingRecord = state.formRecords.get(productForm);
    if (existingRecord?.root?.isConnected) {
      if (existingRecord.root.previousElementSibling !== productForm) {
        productForm.after(existingRecord.root);
      }
      return existingRecord;
    }

    const fragment = template.content.cloneNode(true);
    const root = fragment.querySelector(ROOT_SELECTOR);
    if (!root) return null;
    const record = {
      currentVariant: null,
      productForm,
      root,
      surface: "buy_box",
    };
    if (!bindRoot(record)) return null;
    productForm.after(fragment);
    state.formRecords.set(productForm, record);
    state.rootRecords.set(root, record);
    return record;
  };

  const hideRecord = (record) => {
    const emailInput = record?.root?.querySelector("[data-back-in-stock-email]");
    if (emailInput) state.email = emailInput.value;
    if (record?.root) record.root.hidden = true;
    restorePurchaseControls(record?.productForm);
  };

  const hideAllRecords = () => {
    state.formRecords.forEach(hideRecord);
    getProductForms().forEach(restorePurchaseControls);
    setReady(false);
  };

  const isInViewport = (element) => {
    if (!isVisible(element)) return false;
    const rect = element.getBoundingClientRect();
    return (
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < window.innerHeight &&
      rect.left < window.innerWidth
    );
  };

  const emitImpression = (element, variant, surface, productForm) => {
    if (!variant || variant.available !== false || !isInViewport(element)) return;
    const context = getAnalyticsContext(variant, surface, productForm);
    const key = `${context.product_id}:${context.variant_id}:${surface}`;
    if (state.impressionKeys.has(key)) return;
    state.impressionKeys.add(key);
    track(ANALYTICS_EVENTS.impression, variant, surface, productForm);
  };

  const rootObserver = window.IntersectionObserver
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const record = state.rootRecords.get(entry.target);
          if (entry.isIntersecting && record) {
            emitImpression(entry.target, record.currentVariant, "buy_box", record.productForm);
          }
        });
      })
    : null;

  const observeRootImpression = (record) => {
    if (rootObserver && record.root.dataset.impressionObserved !== "true") {
      record.root.dataset.impressionObserved = "true";
      rootObserver.observe(record.root);
    }
    emitImpression(record.root, record.currentVariant, "buy_box", record.productForm);
  };

  const stickyObserver = window.IntersectionObserver
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const trigger = entry.target;
          if (!entry.isIntersecting || !trigger.hasAttribute("data-back-in-stock-trigger")) return;
          const record = getPreferredRecord();
          if (record) {
            emitImpression(trigger, record.currentVariant, "mobile_sticky", record.productForm);
          }
        });
      })
    : null;

  const observeStickyTriggers = () => {
    document.querySelectorAll("[data-back-in-stock-trigger]").forEach((trigger) => {
      if (stickyObserver && trigger.dataset.backInStockImpressionObserved !== "true") {
        trigger.dataset.backInStockImpressionObserved = "true";
        stickyObserver.observe(trigger);
      }
      const record = getPreferredRecord();
      if (record) {
        emitImpression(trigger, record.currentVariant, "mobile_sticky", record.productForm);
      }
    });
  };

  const pruneRecords = (eligibleForms) => {
    state.formRecords.forEach((record, productForm) => {
      if (productForm.isConnected && eligibleForms.has(productForm) && record.root?.isConnected) {
        return;
      }
      restorePurchaseControls(productForm);
      if (rootObserver && record.root) rootObserver.unobserve(record.root);
      if (record.root?.isConnected) record.root.remove();
      if (record.root) state.rootRecords.delete(record.root);
      state.formRecords.delete(productForm);
    });
  };

  const resetRecord = (record) => {
    const toggle = record.root.querySelector("[data-back-in-stock-toggle]");
    const helper = record.root.querySelector("[data-back-in-stock-helper]");
    const form = record.root.querySelector("[data-back-in-stock-form]");
    if (toggle) {
      toggle.hidden = false;
      toggle.disabled = false;
      toggle.setAttribute("aria-expanded", "false");
    }
    if (helper) helper.hidden = false;
    if (form) form.hidden = true;
    record.surface = "buy_box";
    setMessage(record, "");
  };

  const showRecord = (record, variant) => {
    const previousVariantId = record.root.dataset.variantId || "";
    const currentVariantId = String(variant.id);
    const shouldReset = record.root.hidden || previousVariantId !== currentVariantId;
    record.currentVariant = variant;
    record.root.dataset.variantId = currentVariantId;
    record.root.hidden = false;
    const emailInput = record.root.querySelector("[data-back-in-stock-email]");
    if (emailInput && emailInput.value !== state.email) emailInput.value = state.email;
    if (shouldReset && !state.successfulVariants.has(currentVariantId)) resetRecord(record);
    hidePurchaseControls(record.productForm);
    syncRecordControls(record);
    observeRootImpression(record);
  };

  const openPendingForm = () => {
    const { pendingOpen } = state;
    if (!pendingOpen) return;
    const record = openPreferredRecord(
      { focus: true, scroll: true, surface: pendingOpen.surface },
      pendingOpen.variantId
    );
    if (!record) return;
    state.pendingOpen = null;
    track(ANALYTICS_EVENTS.clicked, record.currentVariant, pendingOpen.surface, record.productForm);
  };

  const render = (hintedVariant = null) => {
    state.renderQueued = false;
    if (!refreshConfiguration()) {
      hideAllRecords();
      return;
    }

    const productForms = getProductForms();
    state.productForms = productForms;
    const eligibleForms = new Set(productForms);
    pruneRecords(eligibleForms);
    let visibleSoldOutRecords = 0;
    const variantHint = hintedVariant || state.latestVariant;

    productForms.forEach((productForm) => {
      const variant = resolveVariant(productForm, variantHint);
      const existingRecord = state.formRecords.get(productForm);
      if (!isVisible(productForm) || !variant || variant.available !== false) {
        if (existingRecord) {
          existingRecord.currentVariant = variant;
          hideRecord(existingRecord);
        } else {
          restorePurchaseControls(productForm);
        }
        return;
      }

      const record = ensureRecord(productForm);
      if (!record) {
        restorePurchaseControls(productForm);
        return;
      }
      showRecord(record, variant);
      visibleSoldOutRecords += 1;
    });

    state.latestVariant = null;
    setReady(visibleSoldOutRecords > 0);
    observeStickyTriggers();
    openPendingForm();
  };

  const scheduleRender = (variant = null) => {
    if (variant) state.latestVariant = variant;
    if (state.renderQueued) return;
    state.renderQueued = true;
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => render(state.latestVariant))
    );
  };

  if (typeof window.subscribe === "function") {
    window.subscribe("variant-change", (event) => scheduleRender(event?.data?.variant || null));
  }

  document.addEventListener("pf:product-context-rebuilt", (event) => {
    scheduleRender(event.detail?.variant || null);
  });
  document.addEventListener("shopify:section:load", () => scheduleRender());
  document.addEventListener(OPEN_EVENT, (event) => {
    const surface = event.detail?.surface || "mobile_sticky";
    const requestedVariantId = String(event.detail?.variantId || "");
    const pendingOpen = { surface, variantId: requestedVariantId };
    state.pendingOpen = pendingOpen;
    window.setTimeout(() => {
      if (state.pendingOpen === pendingOpen) state.pendingOpen = null;
    }, 1500);
    openPendingForm();
    if (state.pendingOpen === pendingOpen) {
      state.latestVariant = null;
      scheduleRender(variantMap.get(requestedVariantId) || null);
    }
  });

  // Vendor scripts and price pills change the page constantly, and each render forces a style
  // and layout pass, so only re-render for changes that can affect a buy box: a form, the
  // config, the selected-variant JSON or a sticky trigger coming or going, nodes swapped
  // inside a form, or a visibility change on a form or one of its ancestors.
  const RELEVANT_NODE_SELECTOR = [
    FORM_WRAPPER_SELECTOR,
    CONFIG_SELECTOR,
    TEMPLATE_SELECTOR,
    "[data-selected-variant]",
    "[data-back-in-stock-trigger]",
  ].join(", ");

  const isRelevantNode = (node) =>
    node instanceof Element &&
    (node.matches(RELEVANT_NODE_SELECTOR) || Boolean(node.querySelector(RELEVANT_NODE_SELECTOR)));

  const isRelevantMutation = (mutation) => {
    const target =
      mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
    if (!target || target.closest(ROOT_SELECTOR)) return false;
    if (mutation.attributeName === "data-back-in-stock-trigger") return true;
    if (mutation.type === "childList") {
      const nodes = [...mutation.addedNodes, ...mutation.removedNodes];
      if (target.closest(FORM_WRAPPER_SELECTOR) && nodes.some((node) => node instanceof Element)) {
        return true;
      }
      return nodes.some(isRelevantNode);
    }
    return state.productForms.some((productForm) => target.contains(productForm));
  };

  const mutationObserver = new MutationObserver((mutations) => {
    if (mutations.some(isRelevantMutation)) scheduleRender();
  });
  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["class", "data-back-in-stock-trigger", "hidden", "inert", "style"],
  });
  window.addEventListener("resize", () => scheduleRender());

  window.plugBackInStock = {
    initialized: true,
    labels: { ...config.labels },
    open: (surface = "buy_box") =>
      Boolean(openPreferredRecord({ focus: true, scroll: true, surface })),
    ready: false,
    refresh: () => scheduleRender(),
  };
  render();
})();
